import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getReservations } from "../../../../../../apis/reservation";
import {
  CalendarIcon,
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
  UserIcon,
  OfficeBuildingIcon,
  ClockIcon,
} from "@heroicons/react/outline";

const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (e) {
    return dateString;
  }
};

const Reservations = () => {
  const { id: resortId } = useParams();
  const [viewType, setViewType] = useState("weekly");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [links, setLinks] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);

  const fetchReservations = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setError(null);
        const res = await getReservations(resortId, viewType, selectedDate, page);
        if (res && res.data) {
          setReservations(res.data.reservations || []);
          setLinks(res.data.links || null);
          setMeta(res.data.meta || null);
        } else {
          setReservations([]);
          setLinks(null);
          setMeta(null);
        }
      } catch (e) {
        setError(e.message || "Failed to load reservations");
      } finally {
        setLoading(false);
      }
    },
    [resortId, viewType, selectedDate]
  );

  const goToPage = (pageUrl) => {
    if (!pageUrl) return;
    try {
      const url = new URL(pageUrl);
      const page = url.searchParams.get("page") || 1;
      fetchReservations(page);
    } catch (e) {
      fetchReservations(1);
    }
  };

  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
  };

  const closeModal = () => {
    setSelectedReservation(null);
  };

  const isEmpty = Array.isArray(reservations) && reservations.length === 0;

  return (
    <div className="reservations-container">
      {/* Header */}
      <div className="reservations-header">
        <div>
          <h2 className="section-title">Reservations</h2>
          <p className="section-subtitle">View and manage all property bookings</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">View Type</label>
            <select value={viewType} onChange={(e) => setViewType(e.target.value)} className="filter-select">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Select Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="filter-input"
            />
          </div>

          <button
            onClick={() => fetchReservations(1)}
            disabled={loading}
            className="search-button"
          >
            <SearchIcon className="w-5 h-5" />
            <span>{loading ? "Loading..." : "Search"}</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="error-alert">
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p className="loading-text">Loading reservations...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && isEmpty && (
        <div className="empty-state">
          <CalendarIcon className="empty-icon" />
          <h3 className="empty-title">No Reservations Found</h3>
          <p className="empty-text">
            Try adjusting the date range or switching between weekly and monthly views
          </p>
        </div>
      )}

      {/* Reservations Table */}
      {!loading && !isEmpty && (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="reservations-table">
              <thead>
                <tr>
                  <th>Reservation #</th>
                  <th>Guest Name</th>
                  <th>Channel</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Room Type</th>
                  <th>Rate Code</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr
                    key={r.reservation_number}
                    onClick={() => handleReservationClick(r)}
                    className="table-row"
                  >
                    <td>
                      <div className="reservation-number">#{r.reservation_number}</div>
                    </td>
                    <td>
                      <div className="guest-cell">
                        <div className="guest-avatar">
                          <UserIcon className="w-4 h-4" />
                        </div>
                        <span className="guest-name">{r.guest_name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="channel-badge">{r.channel}</span>
                    </td>
                    <td>
                      <div className="date-cell">
                        <CalendarIcon className="date-icon" />
                        <span>{formatDate(r.arrival)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="date-cell">
                        <CalendarIcon className="date-icon" />
                        <span>{formatDate(r.departure)}</span>
                      </div>
                    </td>
                    <td>{r.room_type}</td>
                    <td>
                      <span className="rate-badge">{r.rate_code}</span>
                    </td>
                    <td>
                      <span className="status-badge status-confirmed">Confirmed</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta && (
            <div className="pagination">
              <div className="pagination-info">
                Showing page {meta.current_page} of {meta.last_page} — {meta.total} total reservations
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => goToPage(links?.prev)}
                  disabled={!links?.prev}
                  className="pagination-btn"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                  <span>Previous</span>
                </button>
                <span className="page-indicator">
                  {meta.current_page} / {meta.last_page}
                </span>
                <button
                  onClick={() => goToPage(links?.next)}
                  disabled={!links?.next}
                  className="pagination-btn"
                >
                  <span>Next</span>
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reservation Details Modal */}
      {selectedReservation && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  <CalendarIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="modal-title">Reservation Details</h3>
                  <p className="modal-subtitle">#{selectedReservation.reservation_number}</p>
                </div>
              </div>
              <button onClick={closeModal} className="modal-close-btn">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              {/* Guest Information */}
              <div className="detail-section">
                <h4 className="detail-section-title">
                  <UserIcon className="section-icon" />
                  Guest Information
                </h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Guest Name</span>
                    <span className="detail-value">{selectedReservation.guest_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Channel</span>
                    <span className="channel-badge">{selectedReservation.channel}</span>
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="detail-section">
                <h4 className="detail-section-title">
                  <OfficeBuildingIcon className="section-icon" />
                  Booking Details
                </h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Reservation Number</span>
                    <span className="detail-value">#{selectedReservation.reservation_number}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Room Type</span>
                    <span className="detail-value">{selectedReservation.room_type}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Rate Code</span>
                    <span className="rate-badge">{selectedReservation.rate_code}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status</span>
                    <span className="status-badge status-confirmed">Confirmed</span>
                  </div>
                </div>
              </div>

              {/* Stay Information */}
              <div className="detail-section">
                <h4 className="detail-section-title">
                  <ClockIcon className="section-icon" />
                  Stay Information
                </h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Check-in Date</span>
                    <span className="detail-value">{formatDate(selectedReservation.arrival)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Check-out Date</span>
                    <span className="detail-value">{formatDate(selectedReservation.departure)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button onClick={closeModal} className="modal-btn-secondary">
                Close
              </button>
              <button className="modal-btn-primary">
                Edit Reservation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .reservations-container {
          padding: 0;
        }

        .reservations-header {
          margin-bottom: 2rem;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .section-subtitle {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .filters-row {
          display: flex;
          gap: 1rem;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          flex: 1;
          min-width: 150px;
        }

        .filter-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .filter-select,
        .filter-input {
          padding: 0.625rem 0.875rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: white;
        }

        .filter-select:focus,
        .filter-input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .search-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .search-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .search-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .error-alert {
          padding: 1rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 0.75rem;
          color: #dc2626;
          font-size: 0.875rem;
          margin-bottom: 1.5rem;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          text-align: center;
        }

        .empty-icon {
          width: 4rem;
          height: 4rem;
          color: #d1d5db;
          margin-bottom: 1rem;
        }

        .empty-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 0.5rem 0;
        }

        .empty-text {
          font-size: 0.875rem;
          color: #9ca3af;
          margin: 0;
          max-width: 400px;
        }

        .table-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .reservations-table {
          width: 100%;
          border-collapse: collapse;
        }

        .reservations-table thead {
          background: linear-gradient(135deg, #f9fafb, #ffffff);
          border-bottom: 2px solid #e5e7eb;
        }

        .reservations-table th {
          padding: 1rem 1.5rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .reservations-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.875rem;
          color: #374151;
        }

        .table-row {
          cursor: pointer;
          transition: background 0.2s;
        }

        .table-row:hover {
          background: #f9fafb;
        }

        .reservation-number {
          font-weight: 600;
          color: #8b5cf6;
        }

        .guest-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .guest-avatar {
          width: 2rem;
          height: 2rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .guest-name {
          font-weight: 500;
          color: #1f2937;
        }

        .channel-badge {
          display: inline-block;
          padding: 0.25rem 0.625rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .date-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .date-icon {
          width: 1rem;
          height: 1rem;
          color: #9ca3af;
        }

        .rate-badge {
          display: inline-block;
          padding: 0.25rem 0.625rem;
          background: #f3e8ff;
          color: #7c3aed;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.625rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-confirmed {
          background: #d1fae5;
          color: #065f46;
        }

        .pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .pagination-info {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .pagination-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: #8b5cf6;
          color: #8b5cf6;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-indicator {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-container {
          background: white;
          border-radius: 1rem;
          max-width: 48rem;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .modal-body {
          padding: 2rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb {
          background: #8b5cf6;
          border-radius: 4px;
        }

        .detail-section {
          margin-bottom: 2rem;
        }

        .detail-section:last-child {
          margin-bottom: 0;
        }

        .detail-section-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 1rem 0;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f3f4f6;
        }

        .section-icon {
          width: 1.25rem;
          height: 1.25rem;
          color: #8b5cf6;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail-value {
          font-size: 0.9375rem;
          font-weight: 500;
          color: #1f2937;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .modal-btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-btn-secondary:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .modal-btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .modal-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        @media (max-width: 768px) {
          .filters-row {
            flex-direction: column;
          }

          .filter-group {
            width: 100%;
          }

          .search-button {
            width: 100%;
            justify-content: center;
          }

          .pagination {
            flex-direction: column;
            gap: 1rem;
          }

          .detail-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Reservations;
